import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../layouts/PageTitle";
import {
  appApi,
  useGetAllStaffQuery,
  useGetExpensesQuery,
} from "../../../store/appApi";
import { selectUserRole } from "../../../store/authSlice";
import { formatCurrency, formatRoleLabel } from "./imprestConfig";
import { allotImprestApi } from "../../../services/imprest";

const darkPanel =
  "rounded-3xl border border-gray-200 bg-white text-gray-900 shadow-xl shadow-slate-200/70";

const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

const EMPTY_FORM = {
  employeeId: "",
  date: new Date(),
  amount: "",
  remarks: "",
};

const AllotImprest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = useSelector(selectUserRole);

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Already scoped to the active project server-side (x-project-id header).
  const { data: staffData, isLoading: staffLoading } = useGetAllStaffQuery({
    page: 1,
    limit: 500,
  });

  const employees = Array.isArray(staffData?.staff)
    ? staffData.staff.filter((item) => item?.status !== false)
    : [];

  // Current Daybook balance — same rolling-balance computation as the
  // Daybook page, so this page can validate/display it before submitting.
  const { data: latestExpenseData } = useGetExpensesQuery({ page: 1 });
  const latestEntries = Array.isArray(latestExpenseData?.finance)
    ? latestExpenseData.finance
    : [];
  const daybookBalance = useMemo(() => {
    if (!latestEntries.length) return 0;
    const sorted = [...latestEntries].sort(
      (a, b) => new Date(a.created) - new Date(b.created),
    );
    let bal = 0;
    sorted.forEach((entry) => {
      if (entry.type === "receipt") bal += Number(entry.amount);
      else bal -= Number(entry.amount);
    });
    return bal;
  }, [latestEntries]);

  const updateField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleFormKeyDown = (e) => {
    if (e.key === "Enter" && e.target.type !== "submit") {
      e.preventDefault();
    }
  };

  if (role !== "super-admin") {
    return (
      <>
        <PageTitle activeMenu="Allot Imprest" motherMenu="Imprest" />
        <div className="rounded-3xl border border-gray-200 bg-white p-6 text-center text-gray-500 shadow-xl shadow-slate-200/70">
          Only a super-admin can allot imprest to employees.
        </div>
      </>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.employeeId) {
      Swal.fire({
        icon: "warning",
        title: "Select employee",
        text: "Please choose an employee for this allotment.",
      });
      return;
    }

    const numericAmount = Number(form.amount);
    if (!numericAmount || numericAmount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid amount",
        text: "Please enter an amount greater than zero.",
      });
      return;
    }
    if (numericAmount > daybookBalance) {
      Swal.fire({
        icon: "warning",
        title: "Amount too high",
        text: `Only ${formatCurrency(daybookBalance)} is available in the Daybook.`,
      });
      return;
    }

    try {
      setSaving(true);
      await allotImprestApi({
        employeeId: form.employeeId,
        date: form.date.toISOString(),
        amount: numericAmount,
        remarks: form.remarks,
      });

      dispatch(appApi.util.invalidateTags(["Imprest", "Expenses"]));

      Swal.fire({
        icon: "success",
        title: "Allotted",
        text: "Imprest allotted successfully.",
        timer: 1800,
        showConfirmButton: false,
      });

      setForm((prev) => ({ ...EMPTY_FORM, employeeId: prev.employeeId }));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to allot",
        text:
          error?.response?.data?.message ||
          "Something went wrong while allotting imprest.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Allot Imprest" motherMenu="Imprest" />

      <div className="space-y-6">
        <section className={`${darkPanel} relative overflow-hidden p-6`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(169,126,8,0.10),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(226,232,240,0.6),_transparent_40%)]" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
              Imprest
            </div>
            <h1 className="mt-4 text-3xl font-semibold text-gray-900 md:text-4xl">
              Allot Imprest
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500">
              Give an employee petty cash from this project's Daybook. It
              debits the Daybook immediately and credits the employee's own
              imprest balance.
            </p>
            <button
              type="button"
              className="mt-4 rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:text-amber-700"
              onClick={() => navigate("/imprest/admin")}
            >
              View All Employees
            </button>
          </div>
        </section>

        <form
          onSubmit={handleSubmit}
          onKeyDown={handleFormKeyDown}
          className={`${darkPanel} p-5 md:p-6`}
        >
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className={labelClass}>Employee</label>
              <select
                className={inputClass}
                autoComplete="off"
                value={form.employeeId}
                onChange={(event) => updateField("employeeId", event.target.value)}
                disabled={staffLoading}
              >
                <option value="">
                  {staffLoading ? "Loading employees…" : "Select employee"}
                </option>
                {employees.map((employee) => (
                  <option key={employee._id} value={employee._id}>
                    {employee.name} - {formatRoleLabel(employee.role)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Date</label>
              <DatePicker
                selected={form.date}
                onChange={(date) => date && updateField("date", date)}
                className={inputClass}
                autoComplete="off"
                dateFormat="dd/MM/yyyy"
                maxDate={new Date()}
              />
            </div>

            <div>
              <label className={labelClass}>Amount</label>
              <input
                type="number"
                min="0"
                max={daybookBalance}
                step="0.01"
                className={inputClass}
                autoComplete="off"
                value={form.amount}
                onChange={(event) => updateField("amount", event.target.value)}
                placeholder="0.00"
              />
              <p className="mt-1 text-xs text-gray-500">
                Available balance:{" "}
                <span className="font-semibold text-gray-900">
                  {formatCurrency(daybookBalance)}
                </span>
              </p>
            </div>

            <div>
              <label className={labelClass}>Remarks</label>
              <input
                className={inputClass}
                autoComplete="off"
                value={form.remarks}
                onChange={(event) => updateField("remarks", event.target.value)}
                placeholder="Reason / note"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end border-t border-gray-200 pt-5">
            <button
              type="submit"
              disabled={saving || daybookBalance <= 0}
              className="rounded-full bg-amber-500 px-5 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving..." : "Allot Imprest"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AllotImprest;
